import { z } from "zod";
import { fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const Schema = z.object({
  mois: z.array(
    z.object({
      mois: z.number(),
      tvaFacturee: z.number(),
      tvaDeductible: z.number(),
      creditReporteEntrant: z.number(),
      net: z.number(),
      aPayer: z.number(),
      creditReporteSortant: z.number(),
    }),
  ),
  alertes: z.array(
    z.object({ code: z.string(), libelle: z.string(), exposition: z.number() }),
  ),
  syntheseCa: z.array(
    z.object({
      mois: z.number(),
      nombre: z.number(),
      caHtNet: z.number(),
      dontTaxable: z.number(),
      dontExonere: z.number(),
    }),
  ),
  parClient: z.array(
    z.object({ client: z.string(), nombre: z.number(), caHtNet: z.number() }),
  ),
});

export function FacturationTva({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["tva", dossierId, annee],
    `/dossiers/${dossierId}/fiscal/tva?annee=${annee}&creditInitial=0`,
    Schema,
  );

  if (q.isLoading) return <EtatChargement quoi="de la TVA" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <p className="a-faire">Aucun registre sur l'exercice.</p>;

  const v = q.data.valeur;
  const caAnnuel = v.syntheseCa.reduce((s, m) => s + m.caHtNet, 0);
  const tvaAPayer = v.mois.reduce((s, m) => s + m.aPayer, 0);
  const creditFinal = v.mois[11]?.creditReporteSortant ?? 0;

  return (
    <div>
      <p className="resume">
        TVA sur <strong>facturation</strong>, avec report de crédit d'un mois sur
        l'autre (art. 361 CGI). Ce registre <strong>prime</strong> sur le calcul
        par les comptes dans le suivi fiscal.
      </p>

      <Tuiles
        tuiles={[
          { titre: "Chiffre d'affaires HT net", valeur: fcfa(caAnnuel), note: "net des avoirs" },
          { titre: "TVA à payer sur l'exercice", valeur: fcfa(tvaAPayer) },
          { titre: "Crédit reporté au 31/12", valeur: fcfa(creditFinal) },
          {
            titre: "Alertes",
            valeur: v.alertes.length,
            alerte: v.alertes.length > 0,
          },
        ]}
      />

      {v.alertes.length > 0 && (
        <section>
          <h2>Alertes automatiques</h2>
          <ul className="alertes">
            {v.alertes.map((a, i) => (
              <li key={`${a.code}-${i}`}>
                <code>{a.code}</code> {a.libelle}
                {a.exposition > 0 && (
                  <strong> — exposition {fcfa(a.exposition)}</strong>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2>TVA mois par mois</h2>
        <Tableau
          colonnes={[
            { cle: "m", titre: "Mois", rendu: (m) => MOIS[m.mois - 1] },
            { cle: "f", titre: "TVA facturée", montant: true, rendu: (m) => fcfa(m.tvaFacturee) },
            { cle: "d", titre: "TVA déductible", montant: true, rendu: (m) => fcfa(m.tvaDeductible) },
            {
              cle: "ce",
              titre: "Crédit reporté",
              montant: true,
              rendu: (m) => (m.creditReporteEntrant ? fcfa(m.creditReporteEntrant) : "—"),
            },
            { cle: "net", titre: "Net", montant: true, rendu: (m) => fcfa(m.net) },
            { cle: "p", titre: "À payer", montant: true, rendu: (m) => fcfa(m.aPayer) },
            {
              cle: "cs",
              titre: "Nouveau crédit",
              montant: true,
              rendu: (m) => (m.creditReporteSortant ? fcfa(m.creditReporteSortant) : "—"),
            },
          ]}
          lignes={v.mois}
          cle={(m) => String(m.mois)}
        />
      </section>

      <section>
        <h2>Chiffre d'affaires par mois</h2>
        <Tableau
          colonnes={[
            { cle: "m", titre: "Mois", rendu: (m) => MOIS[m.mois - 1] },
            { cle: "n", titre: "Factures", montant: true, rendu: (m) => m.nombre },
            { cle: "ca", titre: "CA HT net", montant: true, rendu: (m) => fcfa(m.caHtNet) },
            { cle: "t", titre: "Dont taxable", montant: true, rendu: (m) => fcfa(m.dontTaxable) },
            { cle: "e", titre: "Dont exonéré", montant: true, rendu: (m) => fcfa(m.dontExonere) },
          ]}
          lignes={v.syntheseCa}
          cle={(m) => String(m.mois)}
        />
      </section>

      <section>
        <h2>Chiffre d'affaires par client</h2>
        <Tableau
          colonnes={[
            { cle: "c", titre: "Client", rendu: (c) => c.client },
            { cle: "n", titre: "Factures", montant: true, rendu: (c) => c.nombre },
            { cle: "ca", titre: "CA HT net", montant: true, rendu: (c) => fcfa(c.caHtNet) },
          ]}
          lignes={v.parClient}
          cle={(c) => c.client}
        />
      </section>

      <section>
        <h2>Facture normalisée électronique</h2>
        <p className="resume">
          <strong>Pas d'API DGI.</strong> Le client édite la facture sur la
          plateforme FNE, puis importe le PDF et le numéro FNE, et valide.
        </p>
      </section>
    </div>
  );
}
